import React, { useState, useEffect } from 'react';
import {
  startOrderStatusBackfill,
  getOrderStatusBackfillStatus,
  OrderStatusBackfillStatus,
} from '../services/OrderStatusBackfill.service';

const OrderStatusBackfill: React.FC = () => {
  const [backfillStatus, setBackfillStatus] = useState<OrderStatusBackfillStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [batchSize, setBatchSize] = useState<string>('200');

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => {
      if (backfillStatus?.isRunning) {
        fetchStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [backfillStatus?.isRunning]);

  const fetchStatus = async () => {
    try {
      const response = await getOrderStatusBackfillStatus();
      if (response.status === 200) {
        setBackfillStatus(response.data || null);
      }
    } catch (err) {
      console.error('Failed to fetch order status backfill status:', err);
    }
  };

  const handleStart = async () => {
    if (backfillStatus?.isRunning) {
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await startOrderStatusBackfill(Number(batchSize) || undefined);
      if (response.status === 200) {
        setSuccess('Fulfillment status backfill started successfully');
        fetchStatus();
      } else {
        setError(response.message || 'Failed to start fulfillment status backfill');
      }
    } catch (err) {
      console.error('Fulfillment status backfill start error:', err);
      setError('Failed to start fulfillment status backfill');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Fulfillment Status Backfill</h2>
          <p className="text-sm text-gray-600 mt-1">
            Derive the canonical fulfillment status for historical orders and stamp completedAt on
            finished books. Label-only and idempotent — safe to re-run.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <input
            type="number"
            min={50}
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-24"
            placeholder="Batch"
          />
          <button
            onClick={handleStart}
            disabled={isLoading || backfillStatus?.isRunning}
            className={`px-4 py-2 rounded-md font-medium ${
              backfillStatus?.isRunning
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Starting...' : backfillStatus?.isRunning ? 'Running...' : 'Start Backfill'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">{success}</div>
      )}

      {backfillStatus && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Status</div>
              <div
                className={`text-lg font-semibold ${
                  backfillStatus.isRunning ? 'text-green-600' : 'text-gray-900'
                }`}
              >
                {backfillStatus.isRunning ? 'Running' : 'Idle'}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Orders Scanned</div>
              <div className="text-lg font-semibold text-gray-900">{backfillStatus.scannedOrders}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Orders Backfilled</div>
              <div className="text-lg font-semibold text-green-600">{backfillStatus.backfilledOrders}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Books Stamped</div>
              <div className="text-lg font-semibold text-green-600">{backfillStatus.backfilledBooks}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="text-sm font-medium text-purple-700">Started At</div>
              <div className="text-lg font-semibold text-purple-900">{formatDate(backfillStatus.startedAt)}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="text-sm font-medium text-purple-700">Finished At</div>
              <div className="text-lg font-semibold text-purple-900">{formatDate(backfillStatus.finishedAt)}</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="text-sm font-medium text-blue-700">Skipped (already set)</div>
              <div className="text-lg font-semibold text-blue-900">{backfillStatus.skippedOrders}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Books Scanned</div>
              <div className="text-lg font-semibold text-gray-900">{backfillStatus.scannedBooks}</div>
            </div>
          </div>

          {Object.keys(backfillStatus.statusCounts || {}).length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-semibold text-gray-700 mb-2">Backfilled status breakdown</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(backfillStatus.statusCounts).map(([status, count]) => (
                  <span
                    key={status}
                    className="px-2 py-1 bg-gray-200 text-gray-800 rounded text-sm font-medium"
                  >
                    {status.replace(/_/g, ' ')}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {backfillStatus.errors?.length ? (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="text-sm font-semibold text-red-700">Errors</div>
              <ul className="text-sm text-red-600 list-disc list-inside">
                {backfillStatus.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default OrderStatusBackfill;
